import { useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { useAuthType } from './useAuthType';
import { useAuthInfoMutation } from './useAuthInfoMutation';
import { useGetTokenInfoMutation } from '../services/getAuthInfo';
import { useAuthMutation } from '../services/login';
import { useSession } from '@/context/SessionContext';

import { isAdmin, isServiceUser } from '@/utils/usersRole';

import type { OnSubmitFunc } from '@/types/form';
import type { LoginFormFields, PrecheckReturn } from '../types';
import { identifyPosthogUser } from '@/lib/posthog/identify';
import { markLoginPending } from '@/lib/posthog/login';
import { safeDecodeRedirect } from '@/utils/authRedirect';

export const useSignIn = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setTokenInfo } = useAuthInfoMutation();
  const { startSession } = useSession();

  const { isPending: isSigningIn, mutate: signInMutate } = useAuthMutation();
  const { mutate: getTokenInfo, isPending: isGettingUserInfo } = useGetTokenInfoMutation();

  const redirectParam = searchParams.get('redirect');
  const state = location.state as { from: Location };
  const from = state?.from || '/';

  const loginPageState = location.state as {
    modal_text: string;
    modal_title: string;
    avoidPrecheck: boolean;
  };

  const { precheck, isUnauthorized, isGettingType, setPrecheckData } = useAuthType(
    loginPageState?.avoidPrecheck
  );

  const onCancelOrBack = () => {
    setPrecheckData(null);
  };

  const onUnauthorizedPrecheck = (data: PrecheckReturn) => {
    setPrecheckData(data);
  };

  const loadingSignIn = isSigningIn || isGettingUserInfo;

  const isLoginReady = Boolean(!isUnauthorized && precheck);

  const checkingRef = useRef<HTMLDivElement | null>(null);
  const loginRef = useRef<HTMLDivElement | null>(null);
  const nodeRef = isLoginReady ? loginRef : checkingRef;

  const signIn: OnSubmitFunc<LoginFormFields> = (formData, setGeneralError, setSuccessMessage) => {
    const redirectToParam = (redirectTo: string) => {
      window.location.replace(redirectTo);
    };

    const force2fa = (email: string, redirectTarget: string | null) => {
      const params = new URLSearchParams({ username: email });
      if (redirectTarget) params.set('redirect', redirectTarget);
      navigate(`/activate2fa?${params.toString()}`, { state });
    };

    const forceServiceUser = () => {
      setSuccessMessage(`
        You are trying to log in with a service user.
        These users don't have access to Mercury.
        Contact your dedicated Customer Success Manager if you need assistance
      `);
    };

    signInMutate(formData, {
      onSuccess: () => {
        markLoginPending();

        getTokenInfo('', {
          onSuccess: (token) => {
            Sentry.setUser({ email: token.sub, id: token.user_id });
            identifyPosthogUser({
              userId: token.user_id,
              email: token.sub,
              role: token.role,
              clientId: token.client_access_id,
              clientName: token.client_name,
              entitlements: token.entitlements,
              isInternal: isServiceUser(token.role),
            });

            const redirectTarget = safeDecodeRedirect(redirectParam);

            if (token.force_2fa && !token.enabled_2fa) {
              force2fa(token.sub, redirectTarget);
              return;
            }

            if (redirectTarget) {
              redirectToParam(redirectTarget);
              return;
            }

            if (isServiceUser(token.role)) {
              forceServiceUser();
              return;
            }

            setTokenInfo(token);
            startSession();

            // Admins live in a separate /admin app. Hard-redirect here so we
            // don't SPA-navigate to `/` and then bounce (which races Playwright
            // waitForURL and can abort the intermediate navigation).
            if (isAdmin(token.role)) {
              window.location.replace('/admin');
              return;
            }

            navigate(from, { replace: true });
          },
          onError: (error) => {
            setGeneralError(error?.message);
          },
        });
      },
      onError: (error) => {
        setGeneralError({ ...error, message: error?.responseError });
      },
    });
  };

  return {
    signIn,
    loadingSignIn,
    loginPageState,
    onCancelOrBack,
    nodeRef,
    isLoginReady,
    authInfo: precheck,
    isGettingType,
    redirectParam,
    onUnauthorizedPrecheck,
  };
};
